/** Lower rank = less complete (used to pick group-level progressStatus). */
const PROGRESS_STATUS_RANK = {
    OPEN: 0,
    PLACED: 1,
    'IN PROGRESS': 2,
    'QC COMPLETE': 3,
    RECEIVED: 4,
    CONSUMED: 5,
    ISSUE: 6,
    REJECTED: 7,
};

function normalizeProgressStatus(status) {
    if (status == null || status === '') {
        return null;
    }
    return String(status).toUpperCase();
}

/**
 * @param {Array<{ progressStatus?: string | null }>} maps
 * @returns {string | null}
 */
function deriveGroupProgressStatus(maps) {
    if (!maps?.length) {
        return null;
    }

    let leastComplete = null;
    let leastRank = Number.POSITIVE_INFINITY;

    for (const map of maps) {
        const normalized = normalizeProgressStatus(map.progressStatus);
        if (normalized != null) {
            const rank = PROGRESS_STATUS_RANK[normalized] ?? Number.POSITIVE_INFINITY - 1;
            if (rank < leastRank) {
                leastRank = rank;
                leastComplete = normalized;
            }
        }
    }

    return leastComplete;
}

/**
 * @param {Array<{ issueStatus?: string | null }>} maps
 * @returns {string | null}
 */
function deriveGroupIssueStatus(maps) {
    if (!maps?.length) {
        return null;
    }

    for (const map of maps) {
        if (map.issueStatus != null && map.issueStatus !== '') {
            return map.issueStatus;
        }
    }

    return null;
}

/**
 * @param {string | null | undefined} value
 * @returns {string | null}
 */
function normalizePartnerLotNumber(value) {
    if (value == null) {
        return null;
    }

    const trimmed = String(value).trim();
    return trimmed === '' ? null : trimmed;
}

/**
 * Lifts partnerLotNumber from nested purchase order maps when non-empty values agree.
 *
 * @param {Array<{ partnerLotNumber?: string | null }>} maps
 * @returns {string | null}
 */
function derivePartnerLotNumber(maps) {
    if (!maps?.length) {
        return null;
    }

    const nonEmptyValues = maps
        .map((map) => normalizePartnerLotNumber(map.partnerLotNumber))
        .filter((value) => value != null);

    if (nonEmptyValues.length === 0) {
        return null;
    }

    const firstValue = nonEmptyValues[0];
    const allAgree = nonEmptyValues.every((value) => value === firstValue);

    return allAgree ? firstValue : null;
}

/**
 * @param {Array<{ unitsPerCase?: number | null, receivedUnitsPerCase?: number | null }>} maps
 * @returns {{ receivedUnitsPerCase: number | null, unitsPerCase: number | null }}
 */
function derivePackagingUnitsPerCase(maps) {
    if (!maps?.length) {
        return { receivedUnitsPerCase: null, unitsPerCase: null };
    }

    const firstUnitsPerCase = maps[0].unitsPerCase ?? null;
    const allSameUnitsPerCase = maps.every((map) => (map.unitsPerCase ?? null) === firstUnitsPerCase);

    const firstReceivedUnitsPerCase = maps[0].receivedUnitsPerCase ?? null;
    const allSameReceived = maps.every((map) => (map.receivedUnitsPerCase ?? null) === firstReceivedUnitsPerCase);

    return {
        receivedUnitsPerCase: allSameReceived ? firstReceivedUnitsPerCase : null,
        unitsPerCase: allSameUnitsPerCase ? firstUnitsPerCase : null,
    };
}

/**
 * @param {object} group
 * @returns {object}
 */
function mapGroceryGroupToTableRow(group) {
    const purchaseOrderMaps = group.purchaseOrderMaps ?? [];
    const { receivedUnitsPerCase, unitsPerCase } = derivePackagingUnitsPerCase(purchaseOrderMaps);

    return {
        id: group.purchaseOrderMapGroupId,
        purchaseOrderMapGroupId: group.purchaseOrderMapGroupId,
        qcrPurchaseOrderMapId: group.qcrPurchaseOrderMapId ?? null,
        itemMasterSKU: group.itemMasterSKU,
        itemMasterName: group.itemMasterName,
        itemNetSuiteID: group.itemNetsuiteID,
        externalSku: group.externalSku,
        qty: group.totalExpectedCaseQuantity,
        progressStatus: group.progressStatus ?? deriveGroupProgressStatus(purchaseOrderMaps),
        issueStatus: group.issueStatus ?? deriveGroupIssueStatus(purchaseOrderMaps),
        issueTypes: group.issueTypes ?? null,
        baseUnitsPerCase: group.unitsPerCase ?? unitsPerCase,
        receivedUnitCount: receivedUnitsPerCase ?? group.receivedUnitCount ?? null,
        unitCountVariance: group.unitCountVariance ?? null,
        casesCounted: group.casesCounted ?? null,
        purchaseOrderMaps,
        isSplit: group.isSplit,
        isProduce: group.isProduce,
        requiresQCReceiving: group.requiresQCReceiving,
        purchasingCaseUom: group.purchasingCaseUom,
        baseUnitUom: group.baseUnitUom,
        ozCs: group.ozCs,
        expirationType: group.expirationType ?? null,
        expiresOn: group.expiresOn ?? null,
        partnerLotNumber: group.partnerLotNumber ?? derivePartnerLotNumber(purchaseOrderMaps),
        statusChangeAuthUser: group.statusChangeAuthUser ?? null,
        casesInspectedExternal: group.casesInspectedExternal ?? null,
        casesInspectedInternal: group.casesInspectedInternal ?? null,
        palletsInspectedExternal: group.palletsInspectedExternal ?? null,
        palletsInspectedInternal: group.palletsInspectedInternal ?? null,
        internalUnitInspection: group.internalUnitInspection ?? {},
        caseCounts: group.caseCounts ?? {},
        externalIssues: group.externalIssues ?? {},
        internalIssues: group.internalIssues ?? {},
        truckID: group.truckID ?? null,
        internalInspectionStatus: group.internalInspectionStatus ?? null,
        externalInspectionStatus: group.externalInspectionStatus ?? null,
        inventoryCountStatus: group.inventoryCountStatus ?? null,
        receivedCases: group.totalCasesReceived ?? null,
    };
}

/**
 * @param {object} group
 * @returns {object}
 */
function mapPackagingGroupToTableRow(group) {
    const packagingPurchaseOrderMaps = group.packagingPurchaseOrderMaps ?? [];
    const { receivedUnitsPerCase, unitsPerCase } = derivePackagingUnitsPerCase(packagingPurchaseOrderMaps);

    return {
        poMapID: group.packagingPurchaseOrderMapGroupId,
        packagingPurchaseOrderMapGroupId: group.packagingPurchaseOrderMapGroupId,
        masterSku: group.masterSku,
        description: group.description,
        type: group.type,
        boxSize: group.boxSize,
        qty: group.totalExpectedCaseQuantity,
        progressStatus: deriveGroupProgressStatus(packagingPurchaseOrderMaps),
        issueStatus: deriveGroupIssueStatus(packagingPurchaseOrderMaps),
        issueTypes: null,
        unitsPerCase,
        receivedUnitsPerCase,
        packagingPurchaseOrderMaps,
        isSplit: group.isSplit,
        partnerLotNumber: derivePartnerLotNumber(packagingPurchaseOrderMaps),
    };
}

/**
 * @param {object[]} groups
 * @param {boolean} isPackaging
 * @returns {object[]}
 */
function mapPurchaseOrderMapGroupRows(groups, isPackaging) {
    if (isPackaging) {
        return groups.map(mapPackagingGroupToTableRow);
    }
    return groups.map(mapGroceryGroupToTableRow);
}

export {
    deriveGroupIssueStatus,
    deriveGroupProgressStatus,
    derivePartnerLotNumber,
    derivePackagingUnitsPerCase,
    mapGroceryGroupToTableRow,
    mapPackagingGroupToTableRow,
    mapPurchaseOrderMapGroupRows,
    PROGRESS_STATUS_RANK,
};
